import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as CasesActions from './cases.actions';
import { type FilterConfig } from '@shared/ui';
import { SortDirection } from '@angular/material/sort';

export const CASES_FEATURE_KEY = 'cases';

export interface State {
  cases: any[];
  currentCaseId: string;
  decrypted?: boolean;
  decryptedCases?: any[];
  currWorkspace: any;
  filters: EntityState<FilterConfig>;
  sorts: { [key: string]: SortDirection };
  pageIndex: number;
  pageSize: number;
}

export const filterAdapter = createEntityAdapter<FilterConfig>({
  selectId: (filter) => filter.name,
});

export const initialState: State = {
  cases: [],
  currentCaseId: undefined,
  decrypted: false,
  decryptedCases: [],
  currWorkspace: undefined,
  filters: filterAdapter.getInitialState(),
  sorts: {},
  pageIndex: 0,
  pageSize: 20,
};

const casesReducer = createReducer(
  initialState,
  on(CasesActions.decrypt, (state, { decrypted }) => ({
    ...state,
    decrypted: decrypted,
  })),
  on(CasesActions.updateCaseData, (state, { id, data, currentUserId }) => {
    let encryptedCase = state.cases.find((_case) => _case.objectId === id);
    const createdByUser = currentUserId === encryptedCase.createdBy.objectId;

    const allFields = state?.currWorkspace.caseType.fields.map((f) => f.name);

    const encryptedFields = state?.currWorkspace.caseType.fields
      .filter((f) => f.encrypted && f.type !== 'case_name')
      .map((f) => f.name);

    const keysToUpdate = encryptedCase
      ? allFields.filter(
          (key) => encryptedCase.data[key] !== data[key] && (!createdByUser ? !encryptedFields.includes(key) : true)
        )
      : [];

    if (keysToUpdate.length > 0) {
      encryptedCase = {
        ...encryptedCase,
        name: keysToUpdate.includes('case_identifier') ? data['case_identifier'] : encryptedCase.name,
        data: {
          ...encryptedCase.data,
          ...keysToUpdate.reduce((obj, key) => {
            obj[key] = data[key];
            return obj;
          }, {}),
        },
      };
    }

    if (createdByUser && encryptedCase.name !== data['case_identifier']) {
      encryptedCase = {
        ...encryptedCase,
        name: data['case_identifier'],
        data: { ...encryptedCase.data, ...data['case_identifier'] },
      };
    }

    return {
      ...state,
      cases: [...(state.cases?.filter((c) => c.objectId !== id) || []), encryptedCase],
    };
  }),
  on(CasesActions.updateDecryptedCaseData, (state, { id, data }) => {
    const decryptedCase = state.decryptedCases.find((_case) => _case.objectId === id);

    const caseToUpdate = {
      ...(decryptedCase || []),
      name: data['case_identifier'] ? data['case_identifier'] : decryptedCase.name,
      data,
    };

    return {
      ...state,
      decryptedCases: [
        ...(state.decryptedCases?.filter((c) => c.objectId !== caseToUpdate.objectId) || []),
        caseToUpdate,
      ],
    };
  }),
  on(CasesActions.addDecryptedCases, (state, { decryptedCases }) => {
    const decryptedCasesToInclude = (decryptedCases || []).filter(
      (c) => !state.decryptedCases.find((_case) => _case.objectId === c.objectId)
    );
    return {
      ...state,
      decryptedCases: [...state.decryptedCases, ...decryptedCasesToInclude],
      decrypted: true,
    };
  }),
  on(CasesActions.casesLoaded, (state, { cases, decryptedCases }) => {
    const casesToInclude = (cases || []).filter((c) => !state.cases.find((_case) => _case.objectId === c.objectId));
    const decryptedCasesToInclude = (decryptedCases || []).filter(
      (c) => !(state.decryptedCases || []).find((_case) => _case.objectId === c.objectId)
    );
    return {
      ...state,
      cases: [...state.cases, ...casesToInclude],
      decryptedCases: [...state.decryptedCases, ...decryptedCasesToInclude],
    };
  }),
  on(CasesActions.currWorkspaceLoaded, (state, { workspace }) => ({
    ...state,
    currWorkspace: workspace,
  })),
  on(CasesActions.setCurrentCaseId, (state, { caseId }) => ({
    ...state,
    currentCaseId: caseId,
  }))
);
export function reducer(state: State | undefined, action: Action) {
  return casesReducer(state, action);
}
